import { useTerminalsState } from "@/States/Terminals";
import { useGlobalAlert } from "@/States/globalAlert";
import { trpc } from "@/Utils/trpc";
import { LibraryPageContext } from "@/pages/library_v2/components/filesView/context";
import {
  FileStatus,
  type LibraryFile,
} from "@/pages/library_v2/components/filesView/types";
import { isSafeFileName } from "@/pages/library_v2/components/filesView/utils";
import { useTrans } from "@/translations";

import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import type { FILE_LOCATION_TYPE } from "server/bucketApi/library/constants.js";
import { twMerge } from "tailwind-merge";

type Props = {
  file: LibraryFile;
  isRenaming: boolean;
  setIsRenaming: (value: boolean) => void;
};

export const FileName: React.FC<Props> = ({
  file,
  isRenaming,
  setIsRenaming,
}) => {
  const { t } = useTrans();
  const { currentTerminal } = useTerminalsState();
  const { setGlobalAlert } = useGlobalAlert();
  const { refetchFiles } = useContext(LibraryPageContext);

  const renameContainerRef = useRef<HTMLDivElement>(null);
  const [renameValue, setRenameValue] = useState(file.name);

  const isNameValid = useMemo(() => isSafeFileName(renameValue), [renameValue]);

  const { mutate: renameFile } = trpc.library.updateFileName.useMutation({
    onSuccess: () => {
      refetchFiles();
      setGlobalAlert({
        type: "success",
        message: "library:fileRenameSuccessful",
      });
    },
    onError: (error) => {
      setGlobalAlert({
        type: "error",
        message: "errors:documentRenameFailed",
      });
      console.error("Error renaming document:", error);
    },
  });

  useEffect(() => {
    setRenameValue(file.name);
  }, [file.name]);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (
        renameContainerRef.current &&
        !renameContainerRef.current.contains(e.target as Node)
      ) {
        setIsRenaming(false);
      }
    };

    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [setIsRenaming]);

  const onRenameFile = useCallback(
    async (_name: string) => {
      await renameFile({
        terminalId: currentTerminal?.id!,
        newFileName: `${_name}.${file.format}`,
        currentFileName: `${file.name}.${file.format}`,
        type: file.status as string as FILE_LOCATION_TYPE,
        folderName: file.status === FileStatus.PUBLISHED ? file.folderName : "",
      });
    },
    [renameFile, currentTerminal?.id, file],
  );

  const onSaveRename = useCallback(() => {
    const trimmed = renameValue.trim();
    if (!trimmed || !isSafeFileName(trimmed)) return;
    setIsRenaming(false);
    onRenameFile(trimmed);
  }, [renameValue, setIsRenaming, onRenameFile]);

  return (
    <div ref={renameContainerRef} className="min-w-0">
      {isRenaming ? (
        <div className="flex min-w-0 flex-col gap-[3px]">
          <div className="flex min-w-0 items-center gap-[5px]">
            <input
              autoFocus
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") onSaveRename();
              }}
              placeholder={t("libraryPage:fileListRenamePlaceholder")}
              className={twMerge(
                "min-w-0 flex-1 rounded border px-2 py-[6px] text-[13px] outline-none",
                !isNameValid && renameValue.length
                  ? "border-red-500"
                  : "border-black",
              )}
            />
            <button
              type="button"
              onClick={onSaveRename}
              disabled={!isNameValid}
              className="cursor-pointer rounded bg-black px-[9px] py-[6px] text-[11.5px] font-semibold text-white disabled:cursor-not-allowed disabled:opacity-40"
            >
              {t("actions:save")}
            </button>
          </div>
          {!isNameValid && !!renameValue.length && (
            <span className="text-[11px] font-normal text-red-500">
              {t("libraryPage:fileUploadNameInvalidChars")}
            </span>
          )}
        </div>
      ) : (
        <span className="fname block truncate text-[13.5px] font-semibold">
          {file.name}
        </span>
      )}
    </div>
  );
};
